import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet, Text, View } from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";

import Colors from "../../../Resources/Color";
import { UISections } from "../../../Resources/Constants";
import Fonts from "../../../Resources/Fonts";
import GlobalStyles from "../../../Resources/Styles";
import LogoImage from "../../Components/LogoImage";
import SyncDomainIndicator from "../../Components/SyncDomainIndicator";
import TutorialLinkSection from "../../Components/TutorialLinkSection";
import UnifiedTabBar from "../../Components/UnifiedTabBar";
import { useScreenDimensions } from "../../_layout";
import { EquivalentsView } from "../equivalents/EquivalentsView";
import { FoodsView } from "../foods/FoodsView";
import { RecipesView } from "../recipes/RecipesView";
import { NutritionPlansView } from "../plans/NutritionPlansView";
import { NutritionOfflineBlockedView } from "../shared/NutritionOfflineBlockedView";
import { COACH_NUTRITION_BOOTSTRAP_RESOURCES } from "../coachNutritionBootstrap";
import {
  navigateCoachNutritionOfflineToDashboard,
  shouldBlockCoachNutritionOffline,
} from "../shared/nutritionOfflineNavigation";

const TAB_KEYS = [
  "foods-string",
  "equivalents-string",
  "recipes-string",
  "nutritionPlans-string",
];

export const CoachNutritionScreen = observer(({ coach }) => {
  const { t } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const nutritionLogoMaxWidth =
    screenSize === "small" ? 88 : screenSize === "medium" ? 128 : 220;
  const isOfflineBlocked = shouldBlockCoachNutritionOffline(coach);
  const selectedTab = TAB_KEYS.includes(coach?.nutritionSelectedTab)
    ? coach.nutritionSelectedTab
    : TAB_KEYS[0];

  useEffect(() => {
    coach?.ensureNutritionResourcesFromCloud?.(
      COACH_NUTRITION_BOOTSTRAP_RESOURCES.coachScreen,
    )?.catch?.(() => {});
  }, [coach]);

  useEffect(() => {
    if (coach?.nutritionSelectedTab !== selectedTab) {
      coach?.setProperty?.("nutritionSelectedTab", selectedTab);
    }
  }, [coach, selectedTab]);

  const tabOptions = useMemo(
    () =>
      TAB_KEYS.map((key) => ({
        key,
        label: t(key),
      })),
    [t],
  );

  const handleGoToDashboard = useCallback(() => {
    navigateCoachNutritionOfflineToDashboard(coach);
  }, [coach]);

  const handleCloseTutorial = useCallback(async () => {
    await coach?.markUISectionAsShown?.(UISections.NUTRITION_V1);
  }, [coach]);

  return (
    <View style={styles.container}>
      <View style={[GlobalStyles.HStack, styles.headerRow]}>
        <View
          style={[
            styles.titleWrap,
            {
              marginRight: screenSize === "small" ? 8 : 12,
            },
          ]}
        >
          <Text style={[Fonts.bold24, styles.title]}>{t("nutrition-string")}</Text>
          <View
            style={[
              styles.betaPill,
              screenSize === "small" ? styles.betaPillSmall : null,
            ]}
          >
            <Text style={styles.betaPillText}>{t("beta-string")}</Text>
          </View>
        </View>
        <View style={styles.syncIndicatorWrap}>
          <SyncDomainIndicator syncController={coach?.nutritionSync} />
        </View>
        <View style={styles.spacer} />
        <LogoImage coach={coach} maxWidth={nutritionLogoMaxWidth} />
      </View>

      {isOfflineBlocked ? (
        <NutritionOfflineBlockedView onGoToDashboard={handleGoToDashboard} />
      ) : (
        <>
          {coach?.shouldShowUISectionInfo?.(UISections.NUTRITION_V1, {
            skipTrialStartGate: true,
          }) ? (
            <TutorialLinkSection
              text="tutorialNutrition-string"
              urlPath="/nutrition"
              marginTop={24}
              marginBottom={0}
              showCloseButton={true}
              onClose={handleCloseTutorial}
            />
          ) : null}
          <UnifiedTabBar
            tabs={tabOptions}
            selectedKey={selectedTab}
            onSelect={(key) => coach?.setProperty?.("nutritionSelectedTab", key)}
            topMargin={24}
            itemGap={screenSize === "small" ? 12 : 24}
          />

          {selectedTab === "foods-string" ? <FoodsView coach={coach} /> : null}
          {selectedTab === "recipes-string" ? <RecipesView coach={coach} /> : null}
          {selectedTab === "nutritionPlans-string" ? (
            <NutritionPlansView coach={coach} />
          ) : null}
          {selectedTab === "equivalents-string" ? (
            <EquivalentsView coach={coach} />
          ) : null}
        </>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.backgroundColor,
  },
  headerRow: {
    width: "100%",
    alignItems: "center",
  },
  titleWrap: {
    position: "relative",
    justifyContent: "center",
    flexShrink: 0,
    overflow: "visible",
  },
  title: {
    position: "relative",
    zIndex: 1,
    fontWeight: "600",
    letterSpacing: -0.4,
  },
  betaPill: {
    position: "absolute",
    zIndex: 2,
    bottom: -7,
    right: -16,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: "rgba(255, 255, 255, 0.82)",
    borderWidth: 1,
    borderColor: "rgba(46, 94, 110, 0.18)",
    shadowColor: Colors.darkBubbleColor,
    shadowOpacity: 0.08,
    shadowRadius: 6,
    shadowOffset: {
      width: 0,
      height: 2,
    },
    transform: [{ rotate: "-7deg" }],
  },
  betaPillSmall: {
    bottom: -6,
    right: -12,
    paddingHorizontal: 5,
    paddingVertical: 2,
  },
  betaPillText: {
    fontSize: 8,
    fontWeight: "800",
    letterSpacing: 1.3,
    textTransform: "uppercase",
    color: Colors.darkBubbleColor,
  },
  spacer: {
    flex: 1,
  },
  syncIndicatorWrap: {
    marginLeft: 4,
  },
});
